import React from 'react';

import { useService } from '@xstate/react';

import { productBuilderMachine } from '../../../../../context/ProductBuilder/machine';
import { getCurrentVariant } from '../../../../../helpers/getCurrentVariant';
import { getRollerHexColour } from '../../../../../helpers/getHexColorCode';
import { getPatternImages } from '../../../../../helpers/getPatternImages';
import { BackRollLightGuardRollerBlind } from './BackRollLightGuardRollerBlind';
import { FasciaLightGuardRollerBlind } from './FasciaLightGuardRollerBlind';
import { FasciaRollerBlind } from './FasciaRollerBlind';
import { FrontRollLightGuardRollerBlind } from './FrontRollLightGuardRollerBlind';

interface RollerProps extends React.SVGProps<SVGSVGElement> {
  fillImage: string;
}

export const RollerBlindFrontRoll = React.forwardRef(
  (
    props: React.SVGProps<SVGSVGElement>,
    setProductImageRef: React.Ref<SVGSVGElement>,
  ) => {
    const [state] = useService(productBuilderMachine);
    const patternUrl = getPatternImages(state);
    const currentVariant = getCurrentVariant(state.context);

    const currentProduct = currentVariant
      ? state.context.products.find(product =>
          product.variants.find(variant => variant.id === currentVariant.id),
        )
      : '';

    const bracketColourTag = currentProduct
      ? currentProduct.tags
          .find(tag => tag.includes('Bracket Colour'))
          ?.replace('Bracket Colour:', '')
      : '';

    const bracketColour =
      typeof bracketColourTag === 'string' && bracketColourTag !== ''
        ? bracketColourTag.trim()
        : 'Silver';
    // const bracketColour = state.context.custom.bracketColour
    //   ? state.context.custom.bracketColour
    //   : 'Silver';

    const bracketColourHex = getRollerHexColour(bracketColour);

    const bottomRailColour = state.context.custom.bottomRailColour
      ? state.context.custom.bottomRailColour
      : 'Silver';

    const bottomRailColourHex = getRollerHexColour(bottomRailColour);
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 554.23 420"
        {...props}
        preserveAspectRatio="none"
      >
        <defs>
          <pattern
            id="rollerFrontRoll"
            patternUnits="userSpaceOnUse"
            width="100"
            height="100"
          >
            <image href={patternUrl} x="0" y="0" width="100" height="100" />
          </pattern>
        </defs>
        <defs>
          <linearGradient
            id="prefix__New_Gradient_Swatch_1"
            x1={-981.69}
            y1={-385.79}
            x2={-981.69}
            y2={-403.86}
            gradientTransform="translate(1258.81 411.92)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset={0.49} stopOpacity={0} />
            <stop offset={1} stopOpacity={0.3} />
          </linearGradient>
          <style>
            {`.prefix__cls-1{fill:url(#rollerFrontRoll)}.prefix__cls-3{fill:url(${bracketColourHex})}`}
          </style>
        </defs>
        <g id="prefix__Blind">
          <path
            fill="url(#rollerFrontRoll)"
            d="M5.57 8.06h543.1v252.62H5.57z"
          />
          <path
            fill="url(#prefix__New_Gradient_Swatch_1)"
            d="M5.57 8.06h543.1v18.06H5.57z"
          />
          <rect
            fill="url(#rollerFrontRoll)"
            x={3.46}
            y={259.56}
            width={547.16}
            height={7.87}
            rx={0.57}
          />
          <path
            fill={bracketColourHex}
            d="M12.85.61V0H0v23.12a.55.55 0 00.55.55h1.6v-3.83h2V25a1.45 1.45 0 001.45 1.45V8.06a1.45 1.45 0 00-1.48 1.45v5.31h-1V7.23h-.97V.61zM541.38 0v.61h10.71v6.62h-1v7.59h-1V9.51a1.45 1.45 0 00-1.45-1.45v18.42a1.45 1.45 0 001.47-1.48v-5.16h2v3.83h1.59a.55.55 0 00.55-.55V0z"
          />
        </g>
      </svg>
    );
  },
);

export const RollerBlindBackRoll = React.forwardRef(
  (
    props: React.SVGProps<SVGSVGElement>,
    setProductImageRef: React.Ref<SVGSVGElement>,
  ) => {
    const [state] = useService(productBuilderMachine);
    const patternUrl = getPatternImages(state);

    const currentVariant = getCurrentVariant(state.context);

    const currentProduct = currentVariant
      ? state.context.products.find(product =>
          product.variants.find(variant => variant.id === currentVariant.id),
        )
      : '';

    const bracketColourTag = currentProduct
      ? currentProduct.tags
          .find(tag => tag.includes('Bracket Colour'))
          ?.replace('Bracket Colour:', '')
      : '';

    const bracketColour =
      typeof bracketColourTag === 'string' && bracketColourTag !== ''
        ? bracketColourTag
        : 'Silver';

    // const bracketColour = state.context.custom.bracketColour
    //   ? state.context.custom.bracketColour
    //   : 'Silver';

    const bracketColourHex = getRollerHexColour(bracketColour);

    const bottomRailColour = state.context.custom.bottomRailColour
      ? state.context.custom.bottomRailColour
      : 'Silver';

    const bottomRailColourHex = getRollerHexColour(bottomRailColour);

    const backRollBlockoutColour =
      state.context.custom.materialType === 'Blockout'
        ? 'white'
        : `url(#rollerBackRoll)`;

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        data-name="Layer 1"
        viewBox="0 0 547 410"
        {...props}
      >
        <defs>
          <pattern
            id="rollerBackRoll"
            patternUnits="userSpaceOnUse"
            width="100"
            height="100"
          >
            <image href={patternUrl} x="0" y="0" width="100" height="100" />
          </pattern>
        </defs>
        <defs>
          <linearGradient
            id="prefix__linear-gradient"
            x1={10.3}
            y1={788.16}
            x2={544}
            y2={788.16}
            gradientTransform="translate(-3.13 -528.11)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset={0.01} stopColor="#b7b8ba" />
            <stop offset={0.29} stopColor="#94989c" />
            <stop offset={0.65} stopColor="#bbbabe" />
            <stop offset={0.87} stopColor="#9a9ca0" />
            <stop offset={1} stopColor="#a3a5a8" />
            <stop offset={1} stopColor="#bfc2c6" />
          </linearGradient>
          <linearGradient
            id="prefix__linear-gradient-2"
            x1={277.05}
            y1={578.34}
            x2={277.05}
            y2={560.28}
            gradientTransform="translate(-3.13 -528.11)"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset={0.49} stopOpacity={0} />
            <stop offset={1} stopOpacity={0.3} />
          </linearGradient>
          <linearGradient
            id="prefix__linear-gradient-3"
            x1={-453.15}
            y1={-837.22}
            x2={-453.15}
            y2={-855.28}
            gradientTransform="rotate(180 -89.565 -411.58)"
            xlinkHref="#prefix__linear-gradient-2"
          />
          <linearGradient
            id="prefix__linear-gradient-4"
            x1={-981.66}
            y1={138.89}
            x2={-981.66}
            y2={120.84}
            gradientTransform="translate(1255.68 -116.19)"
            xlinkHref="#prefix__linear-gradient-2"
          />
        </defs>
        <path fill="url(#rollerBackRoll)" d="M7.17 256.1h533.7v7.9H7.17z" />
        <path fill="url(#rollerBackRoll)" d="M6.88 4.6h534.09v252.6H6.88z" />
        <path fill={backRollBlockoutColour} d="M6.96 4.6h534.01v27.53H6.96z" />
        <path
          fill="url(#prefix__linear-gradient-2)"
          d="M6.88 32.13h534.09v18.1H6.88z"
        />
        <path
          fill="url(#prefix__linear-gradient-3)"
          d="M6.97 14.03h534.1v18.1H6.97v-18.1z"
        />
        <path
          fill="url(#prefix__linear-gradient-4)"
          d="M6.97 4.6h534.1v18.1H6.97z"
        />
        <path
          d="M532.06 0v.76h13.3V9h-1.24v9.4h-1.25v-6.59a1.81 1.81 0 00-1.8-1.8v22.87a1.79 1.79 0 001.8-1.78v-6.47h2.49v4.76h2a.67.67 0 00.68-.68V0zM0 0v28.71a.67.67 0 00.68.68h2v-4.76h2.46v6.47A1.81 1.81 0 007 32.88V10a1.81 1.81 0 00-1.8 1.8v6.6H3.92V9H2.68V.76H16V0z"
          fill={bracketColourHex}
        />
      </svg>
    );
  },
);

export const RollerBlind = (
  props: React.SVGProps<SVGSVGElement>,
  setProductImageRef: React.Ref<SVGSVGElement>,
) => {
  const [state] = useService(productBuilderMachine);
  const {
    custom: { rollDirection, fascia, lightGuards },
  } = state.context;

  const lightGuardsAdded =
    typeof lightGuards !== 'undefined' && typeof lightGuards === 'number'
      ? true
      : false;

  const fasciaAdded =
    typeof fascia !== 'undefined' && typeof fascia === 'number' ? true : false;

  if (
    rollDirection === 'Front Roll' &&
    lightGuardsAdded === false &&
    fasciaAdded === false
  ) {
    return <RollerBlindFrontRoll {...props} ref={setProductImageRef} />;
  } else if (
    rollDirection === 'Front Roll' &&
    lightGuardsAdded !== false &&
    fasciaAdded === false
  ) {
    return (
      <FrontRollLightGuardRollerBlind {...props} ref={setProductImageRef} />
    );
  } else if (
    rollDirection === 'Back Roll' &&
    lightGuardsAdded !== false &&
    fasciaAdded === false
  ) {
    return (
      <BackRollLightGuardRollerBlind {...props} ref={setProductImageRef} />
    );
  } else if (fasciaAdded === true && lightGuardsAdded === false) {
    return <FasciaRollerBlind {...props} ref={setProductImageRef} />;
  } else if (fasciaAdded === true && lightGuardsAdded !== false) {
    return <FasciaLightGuardRollerBlind {...props} ref={setProductImageRef} />;
  } else {
    return <RollerBlindBackRoll {...props} ref={setProductImageRef} />;
  }
};
